/**
 * Booking photo section of the venue page: shows the photo customers see when they book here,
 * where it came from, and lets venue editors upload, replace or remove it.
 */
import { useId } from 'react';

import type { VenueDetail } from '../../../../../api/venues';
import { VisuallyHiddenStatus } from '../../../../shared/VisuallyHiddenStatus';

import { PhotoCardView } from './PhotoCardView';
import { PhotoSectionHeader } from './PhotoSectionHeader';
import { usePhotoLayout } from './use-photo-layout';
import type { VenuePhotoFlow } from './use-venue-photo-flow';
import { useWindowDropGuard } from './use-window-drop-guard';

export interface BookingPhotoSectionProps {
  venue: VenueDetail;
  readOnly: boolean;
  /** Kept by the page, so a half-finished change survives switching tabs. */
  flow: VenuePhotoFlow;
}

export function BookingPhotoSection({ venue, readOnly, flow }: BookingPhotoSectionProps) {
  const headingId = useId();
  const guidanceId = useId();
  const layout = usePhotoLayout();
  useWindowDropGuard();

  return (
    <section aria-labelledby={headingId} style={{ margin: '22px 0 0' }}>
      <PhotoSectionHeader id={headingId} locked={readOnly} />
      <PhotoCardView
        venue={venue}
        readOnly={readOnly}
        layout={layout}
        flow={flow}
        guidanceId={guidanceId}
      />
      <VisuallyHiddenStatus message={flow.announcement} />
    </section>
  );
}
